import { dashboardRouteSearchSchema } from '../../dashboard/dashboardRouteSearch';
import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch, useLocation } from '@tanstack/react-router';

import { defaultCampaignsFilter, type CampaignStatus } from './model';
import {
  parseCampaignsRouteSearch,
  serializeCampaignsRouteSearch,
  type CampaignsRouteFilters,
  type CampaignsRouteSearch,
} from './campaignsRouteSearch';

export function useCampaignsRouteFilters() {
  const scope = useAgencyCampaignContext();
  const pathname = useLocation({ select: (location) => location.pathname });
  const navigate = useNavigate({ from: campaignsFilterRoute(pathname, Boolean(scope)) });
  const search = useSearch({ strict: false });
  const filters = useMemo(() => parseCampaignsRouteSearch(search), [search]);
  const update = useCallback(
    (updater: (current: CampaignsRouteFilters) => CampaignsRouteFilters) => {
      void navigate({
        search: (current: CampaignsRouteSearch) => ({
          ...dashboardRouteSearchSchema(current),
          ...serializeCampaignsRouteSearch(updater(parseCampaignsRouteSearch(current))),
        }),
        replace: true,
      });
    },
    [navigate],
  );
  const setSearch = useCallback(
    (name: string) => {
      update((current) => ({ ...current, name, page: 0 }));
    },
    [update],
  );

  return {
    setAgencies: (agencyIds: string[]) => {
      update((current) => ({ ...current, agencyIds, page: 0 }));
    },
    filters,
    queryFilter: { ...defaultCampaignsFilter, ...filters },
    setBrand: useCallback(
      (brandId: string) => {
        update((current) => ({ ...current, brandId, page: 0 }));
      },
      [update],
    ),
    setSearch,
    setStatuses: (status: CampaignStatus[]) => {
      update((current) => ({ ...current, status, page: 0 }));
    },
    setPagination: (page: number, size: number) => {
      update((current) => ({ ...current, page, size }));
    },
  };
}

function campaignsFilterRoute(pathname: string, scoped: boolean) {
  if (pathname.includes('/brands/'))
    return scoped
      ? ('/a/$agencyId/campaigns/brands/$brandId/details' as const)
      : ('/campaigns/brands/$brandId/details' as const);
  return scoped ? ('/a/$agencyId/campaigns/$section' as const) : ('/campaigns/campaigns' as const);
}
